import React, { useState, useEffect, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  RefreshCw, AlertCircle, Globe, Server,
  ExternalLink, Clock, Users, UsersRound, Layout, Building2, Zap, CreditCard,
  Eye, EyeOff, Loader2, CheckCircle2, ChevronDown, Shield, Key, Trash2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import integrationsService from "../../services/integrationsService";
import WorkvivoDefaultSpaceCard from "./WorkvivoDefaultSpaceCard";
import WorkvivoNotificationMatrix from "./WorkvivoNotificationMatrix";
import WorkvivoInboundKudosToggle from "./WorkvivoInboundKudosToggle";
import WorkvivoSamlSsoCard from "./WorkvivoSamlSsoCard";
import organizationsService from "../../services/organizationsService";
import workvivoService from "../../services/workvivoService";
import ssoService from "../../services/ssoService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import TremendousConnect from "./TremendousConnect";
import ScimProvisioningSection from "./ScimProvisioningSection";
import AuthContext from "../../context/AuthContext";

const IntegrationsSection = () => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { user } = useContext(AuthContext);

  // Fetch organizations with Workvivo status
  const { data: organizations = [], isLoading: orgsLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.list(),
  });

  // Sync Workvivo users for specific org mutation (available for future use)
  // eslint-disable-next-line no-unused-vars
  const syncWorkvivoMutation = useMutation({
    mutationFn: (orgId) => integrationsService.syncWorkvivoUsers(orgId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success(`Successfully synced ${data.users_synced || 0} Workvivo users!`);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Sync all Workvivo organizations mutation
  const syncAllWorkvivoMutation = useMutation({
    mutationFn: () => integrationsService.syncAllWorkvivoOrganizations(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success(`Synced users from ${data.organizations_synced || 0} organizations!`);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Sync Workvivo team memberships mutation
  const syncTeamMembershipsMutation = useMutation({
    mutationFn: () => integrationsService.syncWorkvivoTeamMemberships(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success(`Team memberships synced! Created: ${data.memberships_created || 0}, Updated: ${data.memberships_updated || 0}`);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // ---------------------------------------------------------------------------
  // SSO Configuration
  // ---------------------------------------------------------------------------
  const [showSSOModal, setShowSSOModal] = useState(false);
  const [ssoEditMode, setSSOEditMode] = useState(false);
  const [showSSOSecret, setShowSSOSecret] = useState(false);
  const [showSSODeleteConfirm, setShowSSODeleteConfirm] = useState(false);

  // Fetch SSO configuration
  const { data: ssoConfig, isLoading: ssoLoading, error: ssoError } = useQuery({
    queryKey: ["sso", "config"],
    queryFn: () => ssoService.getConfig(),
    retry: false,
  });

  const ssoConfigured = ssoConfig && !ssoError;

  // SSO form
  const {
    register: registerSSO,
    handleSubmit: handleSSOSubmit,
    reset: resetSSO,
    setValue: setSSOValue,
    watch: watchSSO,
    formState: { errors: ssoErrors }
  } = useForm({
    defaultValues: {
      provider_type: "azure_ad",
      client_id: "",
      client_secret: "",
      tenant_id: "",
      okta_domain: "",
      redirect_uri: `${window.location.origin}/api/v1/sso/callback`,
      is_enabled: true,
    }
  });

  const watchedProvider = watchSSO("provider_type");

  // Create SSO config mutation
  const createSSOMutation = useMutation({
    mutationFn: (data) => ssoService.createConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso", "config"] });
      toast.success("SSO configuration created successfully!");
      closeSSOModal();
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  // Update SSO config mutation
  const updateSSOMutation = useMutation({
    mutationFn: (data) => ssoService.updateConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso", "config"] });
      toast.success("SSO configuration updated successfully!");
      closeSSOModal();
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  // Delete SSO config mutation
  const deleteSSOMutation = useMutation({
    mutationFn: () => ssoService.deleteConfig(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso", "config"] });
      toast.success("SSO configuration removed.");
      closeSSOModal();
      setShowSSODeleteConfirm(false);
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  // Toggle SSO enabled/disabled
  const toggleSSOMutation = useMutation({
    mutationFn: () => {
      if (!ssoConfig) return;
      return ssoService.updateConfig({
        provider_type: ssoConfig.provider_type,
        client_id: ssoConfig.client_id,
        client_secret: "", // backend keeps existing if empty
        tenant_id: ssoConfig.tenant_id || "",
        okta_domain: ssoConfig.okta_domain || "",
        redirect_uri: ssoConfig.redirect_uri,
        is_enabled: !ssoConfig.is_enabled,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sso", "config"] });
      toast.success(`SSO ${ssoConfig?.is_enabled ? "disabled" : "enabled"} successfully!`);
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  const openSSOModal = (edit = false) => {
    setSSOEditMode(edit);
    setShowSSOSecret(false);
    setShowSSODeleteConfirm(false);
    if (edit && ssoConfig) {
      resetSSO({
        provider_type: ssoConfig.provider_type,
        client_id: ssoConfig.client_id,
        client_secret: "",
        tenant_id: ssoConfig.tenant_id || "",
        okta_domain: ssoConfig.okta_domain || "",
        redirect_uri: ssoConfig.redirect_uri,
        is_enabled: ssoConfig.is_enabled,
      });
      // provider_type set via resetSSO
    } else {
      resetSSO({
        provider_type: "azure_ad",
        client_id: "",
        client_secret: "",
        tenant_id: "",
        okta_domain: "",
        redirect_uri: `${window.location.origin}/api/v1/sso/callback`,
        is_enabled: true,
      });
    }
    setShowSSOModal(true);
  };

  const closeSSOModal = () => {
    setShowSSOModal(false);
    setSSOEditMode(false);
    setShowSSOSecret(false);
    setShowSSODeleteConfirm(false);
    resetSSO();
  };

  const onSSOSubmit = (data) => {
    const payload = {
      provider_type: data.provider_type,
      client_id: data.client_id,
      client_secret: data.client_secret,
      tenant_id: data.provider_type === "azure_ad" ? data.tenant_id : null,
      okta_domain: data.provider_type === "okta" ? data.okta_domain : null,
      redirect_uri: data.redirect_uri,
      is_enabled: data.is_enabled,
    };
    if (ssoEditMode) {
      updateSSOMutation.mutate(payload);
    } else {
      createSSOMutation.mutate(payload);
    }
  };

  const maskValue = (val) => {
    if (!val || val.length <= 8) return val || "";
    return val.substring(0, 4) + "****" + val.substring(val.length - 4);
  };

  // Workvivo connect/resync state
  const [showResyncModal, setShowResyncModal] = useState(false);
  const [resyncOrg, setResyncOrg] = useState(null);
  const [resyncResult, setResyncResult] = useState(null);
  const [showConnectWorkvivo, setShowConnectWorkvivo] = useState(false);
  const [connectOrg, setConnectOrg] = useState(null);
  const [connectResult, setConnectResult] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [storedCredentials, setStoredCredentials] = useState(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [showUpdateCredentials, setShowUpdateCredentials] = useState(false);

  // Lock background scrolling while a modal is open
  const anyModalOpen = showSSOModal || showResyncModal || showConnectWorkvivo;
  useEffect(() => {
    if (!anyModalOpen) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [anyModalOpen]);

  // Resync form
  const {
    register: registerResync,
    handleSubmit: handleResyncSubmit,
    reset: resetResync,
    formState: { errors: resyncErrors }
  } = useForm({ defaultValues: { workvivo_base_url: "", workvivo_id: "", access_token: "" } });

  // Connect form
  const {
    register: registerConnect,
    handleSubmit: handleConnectSubmit,
    reset: resetConnect,
    formState: { errors: connectErrors }
  } = useForm({ defaultValues: { workvivo_base_url: "", workvivo_id: "", access_token: "" } });

  // Helper to extract setup result from SuccessResponse or raw response
  const extractResult = (response) => {
    // Service returns SuccessResponse: { message, data: { users_imported, ... } }
    // Or might return the inner data directly
    if (response?.data && typeof response.data === 'object' && 'users_imported' in response.data) {
      return response.data;
    }
    if (response && 'users_imported' in response) {
      return response;
    }
    // Fallback: return whatever we got, wrapped to ensure truthy
    return response?.data || response || { users_imported: 0, teams_imported: 0, spaces_imported: 0 };
  };

  // Resync mutation
  const resyncMutation = useMutation({
    mutationFn: ({ id, credentials }) => organizationsService.resyncWorkvivo(id, credentials),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      const result = extractResult(response);
      setResyncResult(result);
      toast.success(`Sync complete! Users: ${result.users_imported || 0}, Teams: ${result.teams_imported || 0}, Spaces: ${result.spaces_imported || 0}`);
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  // Connect to Workvivo mutation
  const connectWorkvivoMutation = useMutation({
    mutationFn: (data) => workvivoService.completeSetup(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      const result = extractResult(response);
      setConnectResult(result);
      toast.success(`Connected! Users: ${result.users_imported || 0}, Teams: ${result.teams_imported || 0}, Spaces: ${result.spaces_imported || 0}`);
    },
    onError: (error) => { toast.error(formatError(error)); },
  });

  const openResyncModal = async (org) => {
    setResyncOrg(org);
    setResyncResult(null);
    resetResync({ workvivo_base_url: "", workvivo_id: "", access_token: "" });
    setShowResyncModal(true);
    setShowToken(false);
    setShowUpdateCredentials(false);
    setStoredCredentials(null);
    setCredentialsLoading(true);
    try {
      const creds = await organizationsService.getWorkvivoCredentials(org.id);
      setStoredCredentials(creds);
    } catch {
      // No stored credentials – user must enter them
      setShowUpdateCredentials(true);
    } finally {
      setCredentialsLoading(false);
    }
  };
  const closeResyncModal = () => { setShowResyncModal(false); setResyncOrg(null); setResyncResult(null); setShowToken(false); resetResync(); setStoredCredentials(null); setShowUpdateCredentials(false); };
  const onResyncSubmit = (data) => {
    // Only send credential fields that the user actually filled in (override stored)
    const credentials = {};
    if (data.workvivo_base_url) credentials.workvivo_base_url = data.workvivo_base_url;
    if (data.workvivo_id) credentials.workvivo_id = data.workvivo_id;
    if (data.access_token) credentials.access_token = data.access_token;
    resyncMutation.mutate({ id: resyncOrg.id, credentials });
  };
  const onQuickResync = () => {
    // One-click resync using stored credentials (no overrides)
    resyncMutation.mutate({ id: resyncOrg.id, credentials: {} });
  };

  const openConnectWorkvivo = (org) => {
    setConnectOrg(org);
    setConnectResult(null);
    resetConnect({ workvivo_base_url: "", workvivo_id: "", access_token: "" });
    setShowConnectWorkvivo(true);
    setShowToken(false);
  };
  const closeConnectWorkvivo = () => { setShowConnectWorkvivo(false); setConnectOrg(null); setConnectResult(null); setShowToken(false); resetConnect(); };
  const onConnectSubmit = (data) => {
    connectWorkvivoMutation.mutate({
      organization_id: connectOrg.id,
      workvivo_base_url: data.workvivo_base_url,
      workvivo_id: data.workvivo_id,
      access_token: data.access_token,
      import_users: true, import_teams: true, import_spaces: true, setup_bot: false,
    });
  };

  // Filter orgs to current user's org for non-superusers
  const visibleOrgs = user?.is_superuser
    ? organizations
    : organizations.filter(org => org.id === user?.organization_id);

  const workvivoConnectedOrgs = visibleOrgs.filter(org =>
    org.integration_type === 'workvivo' || org.import_stats?.workvivo_connected
  );

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg sm:text-2xl font-bold text-slate-800">Integrations</h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1">Manage third-party integrations and services</p>
      </div>

      {/* SSO Section */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white rounded-2xl shadow-sm">
                <Shield className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Single Sign-On (SSO)</h2>
                <p className="text-sm text-slate-600">OIDC authentication with Azure AD or Okta</p>
              </div>
            </div>
            {ssoConfigured ? (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                ssoConfig.is_enabled
                  ? "bg-green-100 text-green-800"
                  : "bg-amber-100 text-amber-800"
              }`}>
                {ssoConfig.is_enabled ? "Enabled" : "Disabled"}
              </span>
            ) : (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600">
                Not Configured
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {ssoLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : ssoConfigured ? (
            <div className="space-y-4">
              {/* SSO Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Server className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">Provider</span>
                  </div>
                  <p className="text-sm text-slate-900">
                    {ssoConfig.provider_type === "azure_ad" ? "Azure AD" : "Okta"}
                  </p>
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Key className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">Client ID</span>
                  </div>
                  <p className="text-sm text-slate-900 font-mono">{maskValue(ssoConfig.client_id)}</p>
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <ExternalLink className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">Redirect URI</span>
                  </div>
                  <p className="text-sm text-slate-900 font-mono truncate">{ssoConfig.redirect_uri}</p>
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">Discovery URL</span>
                  </div>
                  <p className="text-sm text-slate-900 font-mono truncate">{ssoConfig.discovery_url}</p>
                </div>
              </div>

              {/* SSO Actions */}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => openSSOModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-500 text-white rounded-full hover:from-indigo-700 hover:to-purple-600 transition-all shadow-md"
                >
                  Edit
                </button>
                <button
                  onClick={() => toggleSSOMutation.mutate()}
                  disabled={toggleSSOMutation.isPending}
                  className={`px-4 py-2 border rounded-full transition-colors disabled:opacity-50 ${
                    ssoConfig.is_enabled
                      ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                      : "border-green-300 text-green-700 hover:bg-green-50"
                  }`}
                >
                  {toggleSSOMutation.isPending ? "Updating..." : ssoConfig.is_enabled ? "Disable" : "Enable"}
                </button>
                <button
                  onClick={() => setShowSSODeleteConfirm(true)}
                  disabled={deleteSSOMutation.isPending}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-full hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {deleteSSOMutation.isPending ? "Removing..." : "Remove"}
                </button>
              </div>

              {/* Delete Confirmation */}
              {showSSODeleteConfirm && (
                <div className="mt-2 p-4 bg-red-50 border border-red-200 rounded-2xl">
                  <p className="text-sm text-red-800 mb-3">
                    Are you sure you want to remove the SSO configuration? Users will no longer be able to sign in with SSO.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => deleteSSOMutation.mutate()}
                      disabled={deleteSSOMutation.isPending}
                      className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                    >
                      {deleteSSOMutation.isPending ? "Removing..." : "Yes, Remove"}
                    </button>
                    <button
                      onClick={() => setShowSSODeleteConfirm(false)}
                      className="px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-4">
                Enable Single Sign-On to allow users to log in with their corporate identity provider (Azure AD or Okta).
              </p>
              <button
                onClick={() => openSSOModal(false)}
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-500 text-white rounded-full hover:from-indigo-700 hover:to-purple-600 transition-all shadow-md font-medium"
              >
                Configure SSO
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Authentication Mode Section */}
      {visibleOrgs.length > 0 && (() => {
        const currentOrg = visibleOrgs[0];
        const currentAuthMode = currentOrg?.auth_mode || "password";
        return (
          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-2xl shadow-sm">
                  <Key className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Authentication Mode</h2>
                  <p className="text-sm text-slate-600">Control how users sign in to your organization</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sign-in Method</label>
                <select
                  value={currentAuthMode}
                  onChange={async (e) => {
                    const newMode = e.target.value;
                    if ((newMode === "sso" || newMode === "mixed") && !ssoConfigured) {
                      toast.error("Please configure SSO before selecting this mode");
                      return;
                    }
                    try {
                      await organizationsService.update(currentOrg.id, { auth_mode: newMode });
                      queryClient.invalidateQueries({ queryKey: ["organizations"] });
                      toast.success("Authentication mode updated");
                    } catch (err) {
                      toast.error(formatError(err));
                    }
                  }}
                  className="w-full max-w-xs px-3 py-2 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="password">Password Only</option>
                  <option value="sso" disabled={!ssoConfigured}>SSO Only{!ssoConfigured ? " (Configure SSO first)" : ""}</option>
                  <option value="mixed" disabled={!ssoConfigured}>Mixed (SSO + Password){!ssoConfigured ? " (Configure SSO first)" : ""}</option>
                </select>
              </div>
              <p className="text-sm text-slate-500">
                {currentAuthMode === "password" && "All users sign in with email and password."}
                {currentAuthMode === "sso" && "All users sign in through your identity provider (SSO)."}
                {currentAuthMode === "mixed" && "Users can choose between SSO or password. Each user's preference is remembered after first login."}
              </p>
            </div>
          </div>
        );
      })()}

      {/* SSO Configure/Edit Modal */}
      <AnimatePresence>
        {showSSOModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">
                  {ssoEditMode ? "Edit SSO Configuration" : "Configure Single Sign-On"}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Connect your identity provider for enterprise SSO authentication.
                </p>
              </div>

              <form onSubmit={handleSSOSubmit(onSSOSubmit)} className="p-6 space-y-5">
                {/* Provider Selector */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Identity Provider</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => { setSSOValue("provider_type", "azure_ad"); }}
                      className={`p-4 rounded-full border-2 text-center transition-all ${
                        watchedProvider === "azure_ad"
                          ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 hover:border-slate-300 text-slate-700"
                      }`}
                    >
                      <Shield className="w-8 h-8 mx-auto mb-2" />
                      <span className="text-sm font-medium">Azure AD</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSSOValue("provider_type", "okta"); }}
                      className={`p-4 rounded-full border-2 text-center transition-all ${
                        watchedProvider === "okta"
                          ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 hover:border-slate-300 text-slate-700"
                      }`}
                    >
                      <Key className="w-8 h-8 mx-auto mb-2" />
                      <span className="text-sm font-medium">Okta</span>
                    </button>
                  </div>
                  <input type="hidden" {...registerSSO("provider_type")} />
                </div>

                {/* Client ID */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Client ID</label>
                  <input
                    type="text"
                    {...registerSSO("client_id", { required: "Client ID is required" })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Enter your OIDC client ID"
                  />
                  {ssoErrors.client_id && (
                    <p className="text-xs text-red-600 mt-1">{ssoErrors.client_id.message}</p>
                  )}
                </div>

                {/* Client Secret */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Client Secret</label>
                  <div className="relative">
                    <input
                      type={showSSOSecret ? "text" : "password"}
                      {...registerSSO("client_secret", { required: !ssoEditMode ? "Client Secret is required" : false })}
                      className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder={ssoEditMode ? "Leave blank to keep unchanged" : "Enter your OIDC client secret"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSSOSecret(!showSSOSecret)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                    >
                      {showSSOSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {ssoEditMode && (
                    <p className="text-xs text-slate-500 mt-1">Leave blank to keep the existing secret.</p>
                  )}
                  {ssoErrors.client_secret && (
                    <p className="text-xs text-red-600 mt-1">{ssoErrors.client_secret.message}</p>
                  )}
                </div>

                {/* Tenant ID (Azure AD) */}
                {watchedProvider === "azure_ad" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Tenant ID</label>
                    <input
                      type="text"
                      {...registerSSO("tenant_id", {
                        validate: (value) => watchedProvider === "azure_ad" && !value ? "Tenant ID is required for Azure AD" : true
                      })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder="e.g., 12345678-abcd-efgh-ijkl-123456789012"
                    />
                    {ssoErrors.tenant_id && (
                      <p className="text-xs text-red-600 mt-1">{ssoErrors.tenant_id.message}</p>
                    )}
                  </div>
                )}

                {/* Okta Domain */}
                {watchedProvider === "okta" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Okta Domain</label>
                    <input
                      type="text"
                      {...registerSSO("okta_domain", {
                        validate: (value) => watchedProvider === "okta" && !value ? "Okta Domain is required for Okta" : true
                      })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      placeholder="e.g., your-company.okta.com"
                    />
                    {ssoErrors.okta_domain && (
                      <p className="text-xs text-red-600 mt-1">{ssoErrors.okta_domain.message}</p>
                    )}
                  </div>
                )}

                {/* Redirect URI (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Redirect URI</label>
                  <input
                    type="text"
                    {...registerSSO("redirect_uri", { required: "Redirect URI is required" })}
                    placeholder="https://your-domain.com/api/v1/sso/callback"
                    className="w-full px-3 py-2 border border-slate-200 rounded-full bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Copy this URL to your identity provider's allowed redirect URIs.</p>
                </div>

                {/* Enable SSO */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sso-enabled"
                    {...registerSSO("is_enabled")}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                  />
                  <label htmlFor="sso-enabled" className="text-sm text-slate-700">Enable SSO</label>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <div>
                    {ssoEditMode && (
                      <button
                        type="button"
                        onClick={() => setShowSSODeleteConfirm(true)}
                        className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Configuration
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={closeSSOModal}
                      className="px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createSSOMutation.isPending || updateSSOMutation.isPending}
                      className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-500 text-white rounded-full hover:from-indigo-700 hover:to-purple-600 transition-all shadow-md text-sm font-medium disabled:opacity-50"
                    >
                      {(createSSOMutation.isPending || updateSSOMutation.isPending) ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </span>
                      ) : (
                        ssoEditMode ? "Update" : "Save"
                      )}
                    </button>
                  </div>
                </div>

                {/* Delete confirmation inside modal */}
                {showSSODeleteConfirm && ssoEditMode && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                    <p className="text-sm text-red-800 mb-3">
                      Are you sure? This will permanently remove the SSO configuration.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => deleteSSOMutation.mutate()}
                        disabled={deleteSSOMutation.isPending}
                        className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-sm disabled:opacity-50"
                      >
                        {deleteSSOMutation.isPending ? "Removing..." : "Yes, Remove"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSSODeleteConfirm(false)}
                        className="px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Tremendous Connect Section - Per Organization */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white rounded-2xl shadow-sm">
                <CreditCard className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Tremendous Connect</h2>
                <p className="text-sm text-slate-600">Customer-funded reward payouts per organization</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {orgsLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : visibleOrgs.length > 0 ? (
            <div className="space-y-6">
              <p className="text-sm text-slate-600">
                Connect each organization to their own Tremendous account. They'll manage their own funding and reward budgets.
              </p>
              {visibleOrgs.map((org) => (
                <div key={org.id} className="border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-500" />
                      <span className="font-medium text-slate-800">{org.name}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <TremendousConnect
                      organizationId={org.id}
                      adminEmail={user?.email || "admin@example.com"}
                      adminName={user?.full_name || user?.email?.split('@')[0] || "Admin User"}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl text-center">
              <p className="text-sm text-slate-600">No organizations found. Create an organization first.</p>
            </div>
          )}
        </div>
      </div>

      {/* Workvivo Section */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white rounded-2xl shadow-sm">
                <Globe className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Workvivo Integration</h2>
                <p className="text-sm text-slate-600">Employee engagement platform</p>
              </div>
            </div>
            {workvivoConnectedOrgs.length > 0 && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                {workvivoConnectedOrgs.length} Connected
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {orgsLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : visibleOrgs.length > 0 ? (
            <div className="space-y-4">
              {/* Global Sync Buttons - only when there are connected orgs */}
              {workvivoConnectedOrgs.length > 0 && (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm text-slate-500 flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    Connected organizations sync automatically every day at 11:00 AM EST.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => syncTeamMembershipsMutation.mutate()}
                      disabled={syncTeamMembershipsMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 org-gradient org-btn text-white rounded-full transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Users className={`w-4 h-4 ${syncTeamMembershipsMutation.isPending ? "animate-spin" : ""}`} />
                      {syncTeamMembershipsMutation.isPending ? "Syncing..." : "Sync Team Memberships"}
                    </button>
                    {user?.is_superuser && (
                      <button
                        onClick={() => syncAllWorkvivoMutation.mutate()}
                        disabled={syncAllWorkvivoMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 org-gradient text-white rounded-full transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Zap className={`w-4 h-4 ${syncAllWorkvivoMutation.isPending ? "animate-spin" : ""}`} />
                        {syncAllWorkvivoMutation.isPending ? "Syncing All..." : "Sync All Organizations"}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Organizations Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Organization</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Import Stats</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Last Sync</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {visibleOrgs.map((org) => {
                      const isWorkvivo = org.integration_type === 'workvivo' || org.import_stats?.workvivo_connected;
                      return (
                        <tr key={org.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Building2 className="w-5 h-5 text-slate-400 mr-2" />
                              <span className="text-sm font-medium text-slate-900">{org.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isWorkvivo ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                Connected
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                Not Connected
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isWorkvivo ? (
                              <div className="flex items-center gap-3 text-xs text-slate-600">
                                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{org.import_stats?.imported_users || 0} users</span>
                                <span className="flex items-center gap-1"><UsersRound className="w-3 h-3" />{org.import_stats?.imported_teams || 0} teams</span>
                                <span className="flex items-center gap-1"><Layout className="w-3 h-3" />{org.import_stats?.total_spaces || 0} spaces</span>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                            {isWorkvivo && org.import_stats?.last_sync_at
                              ? new Date(org.import_stats.last_sync_at).toLocaleString()
                              : "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isWorkvivo ? (
                              <button
                                onClick={() => openResyncModal(org)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full hover:bg-green-100 transition-colors"
                                title="Resync from Workvivo"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                                Resync
                              </button>
                            ) : (
                              <button
                                onClick={() => openConnectWorkvivo(org)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 transition-colors"
                                title="Connect to Workvivo"
                              >
                                <Globe className="w-3.5 h-3.5" />
                                Connect
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">No organizations found.</p>
                  <p className="text-sm text-blue-800">Create an organization first to configure Workvivo integration.</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6">
            <WorkvivoDefaultSpaceCard />
          </div>

          <div className="mt-6">
            <WorkvivoInboundKudosToggle
              orgId={user?.is_superuser ? user?.organization_id : undefined}
            />
          </div>

          <div className="mt-6">
            <WorkvivoSamlSsoCard />
          </div>

          <div className="mt-6">
            <WorkvivoNotificationMatrix
              orgId={user?.is_superuser ? user?.organization_id : undefined}
            />
          </div>
        </div>
      </div>

      {/* SCIM Provisioning Section */}
      {user?.organization_id && (
        <ScimProvisioningSection organizationId={user.organization_id} />
      )}

      {/* Connect to Workvivo Modal */}
      <AnimatePresence>
        {showConnectWorkvivo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black bg-opacity-50" onClick={closeConnectWorkvivo} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">Connect to Workvivo</h2>
                    <p className="text-sm text-slate-500">{connectOrg?.name || 'Organization'}</p>
                  </div>
                </div>
              </div>

              {connectResult ? (
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Connected!</h3>
                    <p className="text-sm text-slate-500">Workvivo integration is now active</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-2xl p-4 text-center">
                      <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{connectResult.users_imported || 0}</p>
                      <p className="text-xs text-blue-600">Users</p>
                    </div>
                    <div className="bg-purple-50 rounded-2xl p-4 text-center">
                      <UsersRound className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-purple-700">{connectResult.teams_imported || 0}</p>
                      <p className="text-xs text-purple-600">Teams</p>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl p-4 text-center">
                      <Layout className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-emerald-700">{connectResult.spaces_imported || 0}</p>
                      <p className="text-xs text-emerald-600">Spaces</p>
                    </div>
                  </div>
                  <button onClick={closeConnectWorkvivo} className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all">Done</button>
                </div>
              ) : (
                <form onSubmit={handleConnectSubmit(onConnectSubmit)} className="p-6 space-y-4">
                  <p className="text-sm text-slate-600 mb-4">Enter your Workvivo credentials to connect this organization.</p>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Workvivo Base URL <span className="text-red-500">*</span></label>
                    <input type="url" {...registerConnect("workvivo_base_url", { required: "Base URL is required" })} className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="https://api.workvivo.com" />
                    {connectErrors.workvivo_base_url && <p className="text-xs text-red-500 mt-1">{connectErrors.workvivo_base_url.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Workvivo ID <span className="text-red-500">*</span></label>
                    <input type="text" {...registerConnect("workvivo_id", { required: "Workvivo ID is required" })} className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Your Workvivo tenant ID" />
                    {connectErrors.workvivo_id && <p className="text-xs text-red-500 mt-1">{connectErrors.workvivo_id.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Access Token <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type={showToken ? "text" : "password"} {...registerConnect("access_token", { required: "Access token is required" })} className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Your Workvivo API Token" />
                      <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100">
                        {showToken ? <EyeOff className="w-4 h-4 text-slate-600" /> : <Eye className="w-4 h-4 text-slate-600" />}
                      </button>
                    </div>
                    {connectErrors.access_token && <p className="text-xs text-red-500 mt-1">{connectErrors.access_token.message}</p>}
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button type="button" onClick={closeConnectWorkvivo} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors">Cancel</button>
                    <button type="submit" disabled={connectWorkvivoMutation.isPending} className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                      {connectWorkvivoMutation.isPending ? (<><Loader2 className="w-4 h-4 animate-spin" />Connecting...</>) : (<><Globe className="w-4 h-4" />Connect</>)}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Resync from Workvivo Modal */}
      <AnimatePresence>
        {showResyncModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black bg-opacity-50" onClick={closeResyncModal} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">Resync from Workvivo</h2>
                    <p className="text-sm text-slate-500">{resyncOrg?.name || 'Organization'}</p>
                  </div>
                </div>
              </div>

              {resyncResult ? (
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Sync Complete!</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{resyncResult.users_imported || 0}</p>
                      <p className="text-xs text-blue-600">Users</p>
                    </div>
                    <div className="bg-purple-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-bold text-purple-700">{resyncResult.teams_imported || 0}</p>
                      <p className="text-xs text-purple-600">Teams</p>
                    </div>
                    <div className="bg-green-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">{resyncResult.spaces_imported || 0}</p>
                      <p className="text-xs text-green-600">Spaces</p>
                    </div>
                  </div>
                  {resyncResult.users_deactivated > 0 && (
                    <div className="bg-amber-50 rounded-2xl p-3 mb-4 text-center">
                      <p className="text-sm text-amber-700"><span className="font-semibold">{resyncResult.users_deactivated}</span> users deactivated</p>
                    </div>
                  )}
                  {resyncResult.users_skipped_inactive > 0 && (
                    <div className="bg-gray-50 rounded-2xl p-3 mb-4 text-center">
                      <p className="text-sm text-gray-600"><span className="font-semibold">{resyncResult.users_skipped_inactive}</span> inactive Workvivo users not imported</p>
                    </div>
                  )}
                  {resyncResult.errors && resyncResult.errors.length > 0 && (
                    <div className="bg-red-50 rounded-2xl p-3 mb-4">
                      <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
                      <ul className="text-xs text-red-600 list-disc list-inside">
                        {resyncResult.errors.slice(0, 3).map((err, i) => (<li key={i}>{err}</li>))}
                      </ul>
                    </div>
                  )}
                  <button onClick={closeResyncModal} className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all">Done</button>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  {credentialsLoading ? (
                    <div className="flex items-center justify-center min-h-[240px]">
                      <Loader2 className="w-6 h-6 text-green-600 animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Stored credentials info */}
                      {storedCredentials && (
                        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 space-y-2">
                          <div className="flex items-center gap-2 mb-1">
                            <Shield className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-800">Stored Credentials</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-slate-500">Base URL</span>
                              <p className="font-mono text-slate-800 text-xs truncate">{storedCredentials.base_url}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Workvivo ID</span>
                              <p className="font-mono text-slate-800 text-xs truncate">{storedCredentials.workvivo_id}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Access Token</span>
                              <p className="font-mono text-slate-800 text-xs">{storedCredentials.has_token ? storedCredentials.token_hint : "Not set"}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {!storedCredentials && !credentialsLoading && (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm text-amber-800">No stored credentials found. Please enter credentials below.</span>
                          </div>
                        </div>
                      )}

                      {/* Collapsible Update Credentials section */}
                      <div className="border border-slate-200 rounded-2xl overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setShowUpdateCredentials(!showUpdateCredentials)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                        >
                          <span className="text-sm font-medium text-slate-700">
                            {storedCredentials ? "Update Credentials" : "Enter Credentials"}
                          </span>
                          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${showUpdateCredentials ? "rotate-180" : ""}`} />
                        </button>
                        {showUpdateCredentials && (
                          <form onSubmit={handleResyncSubmit(onResyncSubmit)} className="p-4 space-y-3 border-t border-slate-200">
                            <p className="text-xs text-slate-500">
                              {storedCredentials
                                ? "Leave fields empty to keep the stored values. Only fill in what you want to change."
                                : "Enter your Workvivo credentials to sync this organization."}
                            </p>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Workvivo Base URL</label>
                              <input type="url" {...registerResync("workvivo_base_url")} className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={storedCredentials?.base_url || "https://api.workvivo.com"} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Workvivo ID</label>
                              <input type="text" {...registerResync("workvivo_id")} className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={storedCredentials?.workvivo_id || "Your Workvivo tenant ID"} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Access Token</label>
                              <div className="relative">
                                <input type={showToken ? "text" : "password"} {...registerResync("access_token")} className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500" placeholder={storedCredentials?.has_token ? "Leave empty to keep current token" : "Your Workvivo SCIM token"} />
                                <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100">
                                  {showToken ? <EyeOff className="w-4 h-4 text-slate-600" /> : <Eye className="w-4 h-4 text-slate-600" />}
                                </button>
                              </div>
                            </div>
                            <button type="submit" disabled={resyncMutation.isPending} className="w-full px-4 py-2 org-gradient text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                              {resyncMutation.isPending ? (<><Loader2 className="w-4 h-4 animate-spin" />Syncing...</>) : (<><RefreshCw className="w-4 h-4" />Update & Sync</>)}
                            </button>
                          </form>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-3 pt-2">
                        <button type="button" onClick={closeResyncModal} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors">Cancel</button>
                        {storedCredentials && (
                          <button type="button" onClick={onQuickResync} disabled={resyncMutation.isPending} className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                            {resyncMutation.isPending ? (<><Loader2 className="w-4 h-4 animate-spin" />Syncing...</>) : (<><RefreshCw className="w-4 h-4" />Start Sync</>)}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Configuration Required Component
const ConfigurationRequired = ({ service, envVars }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
    <div className="flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-amber-900 mb-2">
          ⚙️ Configuration Required
        </p>
        <p className="text-sm text-amber-800 mb-3">
          To enable {service} integration, configure the following in your backend .env file:
        </p>
        <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto">
          {envVars}
        </pre>
        <p className="text-xs text-amber-700 mt-2">
          After updating the .env file, restart the backend server.
        </p>
      </div>
    </div>
  </div>
);

export default IntegrationsSection;
